import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { useAuth } from "@/lib/auth";
import { useBrandTitle } from "@/lib/useBrandTitle";
import { BrandMark } from "@/components/onboarding/BrandMark";

const COPY = {
  checking: ["Confirming your payment…", "This only takes a moment."],
  paid: ["Payment received", "Your module is ordered and your subscription is active. Your installer will be in touch to set it up on your network."],
  failed: ["Payment didn't go through", "No charge was completed. You can try again."],
  canceled: ["Payment canceled", "Nothing was charged. You can finish paying whenever you're ready."],
};

export default function PaymentResult({ canceled = false }) {
  const [state, setState] = useState(canceled ? "canceled" : "checking");
  const { user } = useAuth();
  useBrandTitle(user?.brand);

  useEffect(() => {
    if (canceled) return;
    const id = new URLSearchParams(window.location.search).get("session_id");
    if (!id) return setState("failed");
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch { /* keep polling */ }
      if (!stop && ++tries < 15) setTimeout(poll, 2000);
      else if (!stop) setState("failed");
    };
    poll();
    return () => { stop = true; };
  }, [canceled]);

  const [title, text] = COPY[state];
  const Icon = state === "paid" ? CheckCircle2 : state === "checking" ? Loader2 : XCircle;
  return (
    <div className="min-h-screen grid place-items-center bg-[#07090E] p-6">
      <div className="max-w-md text-center fade-up" data-testid={`payment-result-${state}`}>
        {user?.brand && <div className="flex justify-center mb-10"><BrandMark brand={user.brand} /></div>}
        <Icon className={`mx-auto h-10 w-10 ${state === "paid" ? "text-emerald-400" : state === "checking" ? "animate-spin text-slate-400" : "text-rose-400"}`} />
        <h1 className="mt-6 text-3xl font-semibold text-slate-50" data-testid="payment-result-title">{title}</h1>
        <p className="mt-3 text-sm text-slate-400">{text}</p>
        <div className="mt-8 flex justify-center gap-3">
          {(state === "failed" || state === "canceled") && (
            <Button onClick={() => startCheckout().catch(() => (window.location.href = "/login"))} className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="payment-retry-btn">Try payment again</Button>
          )}
          {state !== "checking" && <Button variant="outline" className="border-[#222D3D] bg-transparent" onClick={() => (window.location.href = "/app")} data-testid="payment-open-dashboard-btn">Open dashboard</Button>}
        </div>
      </div>
    </div>
  );
}
